import { useMemo } from 'react'
import { RANKS_DESC } from '../lib/cards'
import { matrixCellLabel, parseRange, setMatrixCell } from '../lib/range'
import type { StrategyAction, StrategyNode } from '../types/solver'

interface RangeGridProps {
  notation?: string
  board?: string[]
  node?: StrategyNode
  action?: StrategyAction
  editable?: boolean
  onNotationChange?: (notation: string) => void
  onComboSelect?: (combo: string) => void
}

const actionColors: Record<StrategyAction['kind'], [number, number, number]> = {
  check: [65, 145, 255],
  bet: [255, 164, 70],
  fold: [118, 132, 140],
  call: [53, 196, 139],
  raise: [247, 91, 91],
  'all-in': [180, 107, 255],
}

function comboCell(combo: string): number {
  const firstRank = RANKS_DESC.indexOf(combo[0])
  const secondRank = RANKS_DESC.indexOf(combo[2])
  const high = Math.min(firstRank, secondRank)
  const low = Math.max(firstRank, secondRank)
  if (high === low) return high * 13 + low
  const suited = combo[1] === combo[3]
  return suited ? high * 13 + low : low * 13 + high
}

export function RangeGrid({ notation, board = [], node, action, editable, onNotationChange, onComboSelect }: RangeGridProps) {
  const configMatrix = useMemo(() => {
    if (!notation) return null
    try {
      return parseRange(notation, board).matrix
    } catch {
      return null
    }
  }, [notation, board])

  const resultCells = useMemo(() => {
    if (!node || !action) return null
    const cells = Array.from({ length: 169 }, () => ({ weighted: 0, reach: 0, combos: [] as string[] }))
    Object.entries(node.rangeStrategy.combos).forEach(([combo, strategy]) => {
      const index = comboCell(combo)
      const frequency = strategy.actions.find((candidate) => candidate.actionId === action.id)?.frequency ?? 0
      cells[index].weighted += frequency * strategy.reachProbability
      cells[index].reach += strategy.reachProbability
      if (strategy.reachProbability > 0) cells[index].combos.push(combo)
    })
    return cells.map((cell) => ({
      value: cell.reach > 0 ? cell.weighted / cell.reach : 0,
      combos: cell.combos,
    }))
  }, [node, action])

  const color = action ? actionColors[action.kind] : [36, 187, 135]

  return (
    <div className="range-grid" role="grid" aria-label="13×13ポーカーレンジ">
      {Array.from({ length: 169 }, (_, index) => {
        const row = Math.floor(index / 13)
        const col = index % 13
        const label = matrixCellLabel(row, col)
        const value = resultCells?.[index].value ?? configMatrix?.[index] ?? 0
        const combos = resultCells?.[index].combos ?? []
        const alpha = value <= 0 ? 0.035 : 0.16 + value * 0.72
        const style = { backgroundColor: `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${alpha})` }
        const onClick = () => {
          if (editable && notation && onNotationChange) {
            const nextWeight = ((Math.round(value * 4) + 1) % 5) / 4
            onNotationChange(setMatrixCell(notation, row, col, nextWeight))
          } else if (combos.length && onComboSelect) {
            onComboSelect(combos[0])
          }
        }
        return (
          <button
            type="button"
            role="gridcell"
            className={`range-cell ${value > 0 ? 'is-active' : ''}`}
            style={style}
            onClick={onClick}
            disabled={!editable && combos.length === 0}
            aria-label={`${label} ${Math.round(value * 100)}%`}
            key={label}
          >
            <span>{label}</span>
            <small>{Math.round(value * 100)}</small>
          </button>
        )
      })}
    </div>
  )
}
